import { useState } from "react";
import { FACTIONS } from "@/data";
import type { ForceList } from "@/types/force";
import type { FactionId } from "@/types/game-data";
import { formatRelativeTime } from "@/utils/formatTime";
import { calculateForceTotalCost } from "@/utils/points";
import { DeleteConfirmDialog } from "./DeleteConfirmDialog";

interface RosterCardProps {
	roster: ForceList;
	onOpen: () => void;
	onDelete: () => void;
}

/** Map faction IDs to their CSS custom property color values */
const FACTION_COLORS: Record<FactionId, string> = {
	taskforce: "var(--color-faction-taskforce)",
	militia: "var(--color-faction-militia)",
	russian: "var(--color-faction-russian)",
	"law-enforcement": "var(--color-faction-law-enforcement)",
	"criminal-element": "var(--color-faction-criminal-element)",
};

export function RosterCard({ roster, onOpen, onDelete }: RosterCardProps) {
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

	const faction = FACTIONS.find((f) => f.id === roster.faction);
	const factionColor = FACTION_COLORS[roster.faction] ?? "var(--color-border-default)";
	const totalCost = calculateForceTotalCost(roster);
	const elementCount = roster.elements.length;
	const vehicleCount = roster.vehicles.length;
	const isOverBudget = roster.maxPoints > 0 && totalCost > roster.maxPoints;

	return (
		<>
			<div
				className="group relative bg-bg-secondary border border-border-default rounded-lg overflow-hidden hover:border-accent-primary/40 active:border-accent-primary/40 transition-all cursor-pointer"
				onClick={onOpen}
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === " ") {
						e.preventDefault();
						onOpen();
					}
				}}
				tabIndex={0}
				role="button"
			>
				{/* Faction color accent — left border */}
				<div
					className="absolute left-0 top-0 bottom-0 w-1 rounded-l-lg"
					style={{ backgroundColor: factionColor }}
				/>

				<div className="pl-4 pr-3 py-3 lg:py-3">
					{/* Top row: name + delete button */}
					<div className="flex items-start justify-between gap-2 mb-1.5">
						<h3 className="text-sm font-semibold text-text-primary truncate group-hover:text-accent-primary transition-colors">
							{roster.name || "Unnamed Force"}
						</h3>
						<button
							type="button"
							onClick={(e) => {
								e.stopPropagation();
								setShowDeleteConfirm(true);
							}}
							className="shrink-0 text-text-muted hover:text-accent-danger transition-colors opacity-100 lg:opacity-0 lg:group-hover:opacity-100 cursor-pointer p-1 min-w-[44px] min-h-[44px] lg:min-w-0 lg:min-h-0 lg:p-0.5 flex items-center justify-center"
							title="Delete roster"
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="16"
								height="16"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							>
								<polyline points="3 6 5 6 21 6" />
								<path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
							</svg>
						</button>
					</div>

					{/* Faction name */}
					<div className="text-xs text-text-muted mb-2">
						{faction?.name ?? roster.faction}
					</div>

					{/* Stats row */}
					<div className="flex items-center gap-3 text-xs">
						{/* Point cost */}
						<span
							className={`font-mono font-bold ${
								isOverBudget ? "text-accent-danger" : "text-accent-secondary"
							}`}
						>
							{totalCost}r
							{roster.maxPoints > 0 && (
								<span className="text-text-muted font-normal">
									{" "}
									/ {roster.maxPoints}r
								</span>
							)}
						</span>

						{/* Separator */}
						<span className="text-border-default">|</span>

						{/* Element count */}
						<span className="text-text-muted">
							{elementCount} element{elementCount !== 1 ? "s" : ""}
						</span>

						{/* Vehicle count (only show if >0) */}
						{vehicleCount > 0 && (
							<span className="text-text-muted">
								{vehicleCount} vehicle{vehicleCount !== 1 ? "s" : ""}
							</span>
						)}
					</div>

					{/* Last edited */}
					<div className="text-xs text-text-muted mt-1.5">
						Edited {formatRelativeTime(roster.updatedAt)}
					</div>
				</div>
			</div>

			{/* Delete confirmation dialog */}
			{showDeleteConfirm && (
				<DeleteConfirmDialog
					rosterName={roster.name || "Unnamed Force"}
					onConfirm={() => {
						setShowDeleteConfirm(false);
						onDelete();
					}}
					onCancel={() => setShowDeleteConfirm(false)}
				/>
			)}
		</>
	);
}
